import { useEffect, useState, type FormEvent } from 'react';
import { validatePassword } from '@pos/core';
import { useSession } from '../../app/current.tsx';
import { mfaFactorForPasswordChange, verifyMfaCode } from '../../lib/mfa.ts';
import { useUpdatePending } from '../pwa/UpdatePrompt.tsx';
import { supabase } from '../../lib/supabase.ts';
import { changeOwnerPassword } from '../../services/session.ts';
import { Alert, Button, Field, Input, errorMessage, PasswordInput } from '../../ui/index.tsx';
import { Dialog } from '../../ui/Dialog.tsx';

/** Cambiar la contraseña del dueño. forced: no se puede cerrar (contraseña temporal del alta). */
export function ChangePasswordDialog({ open, forced, onClose }: { open: boolean; forced?: boolean; onClose: () => void }) {
  const { membership } = useSession();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [mfaFactor, setMfaFactor] = useState<string | null>(null);
  const [code, setCode] = useState('');

  useEffect(() => {
    if (open) {
      setPassword('');
      setConfirm('');
      setCode('');
      setError(null);
      setDone(false);
      void mfaFactorForPasswordChange().then(setMfaFactor, () => setMfaFactor(null));
    }
  }, [open]);

  const submit = async (e?: FormEvent) => {
    e?.preventDefault();
    const invalid = validatePassword(password) ?? (password !== confirm ? 'Las contraseñas no coinciden.' : null);
    if (invalid) return setError(invalid);
    if (!navigator.onLine) return setError('Para cambiar la contraseña hace falta conexión a internet.');
    setBusy(true);
    setError(null);
    try {
      if (mfaFactor) {
        await verifyMfaCode(mfaFactor, code);
        setMfaFactor(null);
      }
      await changeOwnerPassword(membership, password);
      setDone(true);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      dismissible={!forced || done}
      title={forced ? 'Elegí tu contraseña' : 'Cambiar mi contraseña'}
      footer={
        done ? (
          <Button onClick={onClose}>Listo</Button>
        ) : (
          <>
            {!forced && (
              <Button variant="secondary" onClick={onClose}>
                Cancelar
              </Button>
            )}
            <Button onClick={() => void submit()} loading={busy}>
              Guardar
            </Button>
          </>
        )
      }
    >
      {done ? (
        <Alert tone="success">Contraseña cambiada. La próxima vez entrá con la nueva (también sin conexión).</Alert>
      ) : (
        <form className="space-y-3" onSubmit={submit}>
          {forced && <p className="text-sm text-slate-600">Entraste con una contraseña temporal. Elegí una nueva que solo sepas vos.</p>}
          <Field label="Nueva contraseña" hint="Al menos 8 caracteres.">
            {(id) => <PasswordInput id={id} autoComplete="new-password" autoFocus value={password} onChange={(e) => setPassword(e.target.value)} />}
          </Field>
          <Field label="Repetila">
            {(id) => <PasswordInput id={id} autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} />}
          </Field>
          {mfaFactor && (
            <Field label="Código de verificación" hint="Tu cuenta tiene verificación en dos pasos: escribí los 6 números de tu app de códigos.">
              {(id) => <Input id={id} inputMode="numeric" autoComplete="one-time-code" value={code} onChange={(e) => setCode(e.target.value)} />}
            </Field>
          )}
          {error && <Alert tone="error">{error}</Alert>}
          <button type="submit" className="hidden" aria-hidden="true" tabIndex={-1} />
        </form>
      )}
    </Dialog>
  );
}

/** Si el dueño entró con la contraseña temporal del alta, le pide cambiarla. */
export function ForcePasswordChange() {
  const [needed, setNeeded] = useState(false);
  // Primero se actualiza la app (el aviso de versión nueva quedaría tapado por este diálogo).
  const updatePending = useUpdatePending();
  useEffect(() => {
    void supabase.auth.getUser().then(({ data }) => setNeeded(data.user?.user_metadata?.must_change_password === true));
  }, []);
  return <ChangePasswordDialog open={needed && !updatePending} forced onClose={() => setNeeded(false)} />;
}
